import { result, findWhere, without } from 'underscore';

export const getDsaDefaultWebsiteLanguage = (permissions) => {
  const isDomesticSimplifiedChineseEnabled = result(permissions, 'IsDomesticSimplifiedChineseEnabled');
  const defaultLanguage = isDomesticSimplifiedChineseEnabled ? 'SimplifiedChinese' : 'English';
  return defaultLanguage;
};

export const getDSAWebsiteLanguages = (permissions) => {
  const DSAEnableInFrenchGerman = result(permissions, 'IsDynamicSearchAdsV1EnabledInFRAndDE');
  const IsEnabledForDSAInCHandNLandITandESandSE = result(permissions, 'IsEnabledForDSAInCHandNLandITandESandSE');
  const IsSimplifiedChineseLanguageEnabledInMultipleLanguage = result(permissions, 'IsSimplifiedChineseLanguageEnabledInMultipleLanguage');
  const IsDomesticSimplifiedChineseEnabled = result(permissions, 'IsDomesticSimplifiedChineseEnabled');

  let languages = [
    'English',
    'Polish',
    'Portuguese',
    'Czech',
    'Romanian',
    'Hungarian',
    'Greek',
    'Slovak',
    'Bulgarian',
    'Croatian',
    'Lithuanian',
    'Slovenian',
    'Estonian',
    'Latvian',
    'Maltese',
    'Danish',
    'Finnish',
    'Norwegian',
    'Turkish',
    'Serbian',
    'Bosnian',
    'Albanian',
    'Macedonian',
    'Icelandic',
    'Japanese',
    'Hebrew',
    'Arabic',
    'Russian',
    'TraditionalChinese',
    'Hindi',
    'Malay',
    'Thai',
    'Indonesian',
    'Tagalog',
    'Vietnamese',
  ];

  if (DSAEnableInFrenchGerman) {
    languages = languages.concat(['French', 'German']);
  }

  if (IsEnabledForDSAInCHandNLandITandESandSE) {
    languages = languages.concat(['Spanish', 'Italian', 'Dutch', 'Swedish']);
  }

  if (IsSimplifiedChineseLanguageEnabledInMultipleLanguage) {
    languages = languages.concat(['SimplifiedChinese']);
  }

  if (IsDomesticSimplifiedChineseEnabled) {
    languages = ['SimplifiedChinese'];
  }

  if (!(IsSimplifiedChineseLanguageEnabledInMultipleLanguage || IsDomesticSimplifiedChineseEnabled)) {
    languages = without(languages, 'SimplifiedChinese');
  }

  return languages;
};

export const targetingSourceOptions = {
  SystemIndex: 'SystemIndex',
  AdvertiserSuppliedUrls: 'AdvertiserSuppliedUrls',
  All: 'All',
};

export const targetingSourceOptionStrings = {
  [targetingSourceOptions.SystemIndex]: _TL_('Use Bing\'s index of my website'),
  [targetingSourceOptions.AdvertiserSuppliedUrls]: _TL_('Use URLs from my page feed only'),
  [targetingSourceOptions.All]: _TL_('Use URLs from both Bing\'s index of my website and my page feed'),
};

export const defaultTargetingSource = targetingSourceOptions.SystemIndex;

export const dsaDefaultTextEnabled = true;

export const dsaTargetOptions = {
  Category: 'Category',
  All: 'All',
  'Specific-ExactUrl': 'Specific-ExactUrl',
  'Specific-Rules': 'Specific-Rules',
  CustomLabel: 'CustomLabel',
};

export const dsaTargetOptionStrings = {
  [dsaTargetOptions.Category]: _TL_('Target categories of webpages'),
  [dsaTargetOptions.All]: _TL_('Target all webpages'),
  [dsaTargetOptions.Specific]: _TL_('Target specific webpages'),
  [dsaTargetOptions.CustomLabel]: _TL_('Target custom labels from your page feed(s)'),
};

const getDefaultDsaSettingsForCampaign = ({ permissions }) => {
  const dsaDefaultWebsiteLanguage = getDsaDefaultWebsiteLanguage(permissions);

  const defaultSearchDynamic = {
    selectedWebsiteLanguage: dsaDefaultWebsiteLanguage,
    selectedTargetingSource: defaultTargetingSource,
    dynamicTextEnabled: dsaDefaultTextEnabled,
    websites: [],
  };

  return defaultSearchDynamic;
};

export const modelToViewModelForDsaSettings = ({ campaignModel, permissions }) => {
  const dsaSettings = findWhere(campaignModel.CampaignSettings, { '@odata.type': '#Model.DynamicSearchAdsSetting' });

  const hasDsaSettings = dsaSettings ? !!dsaSettings.DomainName : false;

  const viewModel = hasDsaSettings ? {
    selectedWebsiteLanguage: dsaSettings.Language,
    selectedTargetingSource: dsaSettings.Source,
    dynamicTextEnabled: dsaSettings.DynamicDescriptionEnabled,
    websites: [dsaSettings.DomainName],
  } : getDefaultDsaSettingsForCampaign({ permissions });

  return {
    hasDsaSettings,
    disableDsaSettings: hasDsaSettings,
    ...viewModel,
  };
};

export const bulkModelToViewModelForDsaSettings = ({ campaignModel, permissions }) => {
  const hasDsaSettings = !!campaignModel.DomainName;

  const viewModel = hasDsaSettings ? {
    selectedWebsiteLanguage: campaignModel.LanguageCode,
    selectedTargetingSource: campaignModel.Source,
    dynamicTextEnabled: campaignModel.DynamicDescriptionEnabled,
    websites: [campaignModel.DomainName],
  } : getDefaultDsaSettingsForCampaign({ permissions });

  return {
    hasDsaSettings,
    disableDsaSettings: hasDsaSettings,
    ...viewModel,
  };
};

export const viewModelToBulkModelForDsaSettings = ({ campaignViewModel, permissions = {} }) => {
  const dsaSettings = {
    DomainName: campaignViewModel.websites.join(),
    ...(result(permissions.dynamic, 'DSADynamicDescriptionPhase1') && { DynamicDescriptionEnabled: campaignViewModel.dynamicTextEnabled }),
    LanguageCode: campaignViewModel.selectedWebsiteLanguage,
    Source: campaignViewModel.selectedTargetingSource,
    // Page feeds
    PageFeedIds: campaignViewModel.selectedTargetingSource !== targetingSourceOptions.SystemIndex ? campaignViewModel.pageFeeds.map(feed => feed.key) : [],
  };

  return dsaSettings;
};
